import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './companydashboard.css';

const CompanyDashboard = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('overview');
    const [companyData, setCompanyData] = useState({
        name: 'Tech Solutions Ltd',
        plan: 'Professional',
        status: 'Active',
        nextBilling: '2024-02-15',
        talentsContacted: 12,
        successfulHires: 3
    });
    const [paymentData, setPaymentData] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [contactedTalents, setContactedTalents] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);

    const formatStatus = (status) =>
        (status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1);

    // Load payment data and update company data
    useEffect(() => {
        const savedPaymentData = localStorage.getItem('paymentData');
        if (savedPaymentData) {
            const payment = JSON.parse(savedPaymentData);
            setPaymentData(payment);

            setCompanyData(prev => ({
                ...prev,
                name: payment.companyData.companyName,
                plan: payment.plan.charAt(0).toUpperCase() + payment.plan.slice(1),
                status: 'Active',
                nextBilling: payment.expiresAt?.split(' ')[0]
                    || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            }));
        }

        if (location.state?.message) {
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 5000);
        }
    }, [location.state]);

    useEffect(() => {
        const subscriptionId = paymentData?.subscriptionId;
        if (!subscriptionId) return;

        let cancelled = false;
        setContactsLoading(true);

        fetch(`https://server.abiatechhub.com/talent-contacts/${encodeURIComponent(subscriptionId)}`)
            .then(res => res.json())
            .then(result => {
                if (cancelled || !result.success) return;

                const mapped = (result.data || []).map(row => ({
                    id: row.id,
                    name: row.talent_name,
                    title: row.talent_title,
                    status: formatStatus(row.status),
                    date: row.contact_date ? String(row.contact_date).split(' ')[0] : '',
                    rating: row.rating || 0,
                    slug: row.talent_id,
                }));

                setContactedTalents(mapped);
                setCompanyData(prev => ({
                    ...prev,
                    talentsContacted: result.stats?.contacted ?? mapped.length,
                    successfulHires: result.stats?.hired ?? mapped.filter(t => t.status === 'Hired').length,
                }));
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setContactsLoading(false);
            });

        return () => { cancelled = true; };
    }, [paymentData?.subscriptionId]);

    const tabs = [
        { id: 'overview', name: 'Overview', icon: 'fas fa-chart-pie' },
        { id: 'talents', name: 'My Talents', icon: 'fas fa-users' },
        { id: 'messages', name: 'Messages', icon: 'fas fa-envelope' },
        { id: 'settings', name: 'Settings', icon: 'fas fa-cog' }
    ];

    return (
        <div className="company-dashboard">
            <div className="dashboard-header">
                <div className="container">
                    {showSuccessMessage && (
                        <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                            <i className="fas fa-check-circle me-2"></i>
                            <strong>Payment Successful!</strong> Your account has been activated and you now have access to our talent pool.
                            <button type="button" className="btn-close" onClick={() => setShowSuccessMessage(false)}></button>
                        </div>
                    )}
                    <div className="row align-items-center">
                        <div className="col-lg-8">
                            <h1 className="dashboard-title">Company Dashboard</h1>
                            <p className="dashboard-subtitle">
                                Welcome back, {companyData.name}! Manage your talent hiring process.
                            </p>
                        </div>
                        <div className="col-lg-4 text-end">
                            <div className="plan-badge">
                                <span className="plan-name">{companyData.plan} Plan</span>
                                <span className="plan-status">{companyData.status}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="dashboard-content">
                <div className="container">
                    <div className="row">
                        {/* Sidebar */}
                        <div className="col-lg-3">
                            <div className="dashboard-sidebar">
                                <nav className="sidebar-nav">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                                            onClick={() => setActiveTab(tab.id)}
                                        >
                                            <i className={tab.icon}></i>
                                            <span>{tab.name}</span>
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="col-lg-9">
                            <div className="dashboard-main">
                                {activeTab === 'overview' && (
                                    <div className="tab-content">
                                        <div className="content-header">
                                            <h2>Overview</h2>
                                            <p>Your hiring activity and account summary</p>
                                        </div>

                                        {/* Stats Cards */}
                                        <div className="row g-4 mb-4">
                                            <div className="col-md-4">
                                                <div className="stat-card">
                                                    <div className="stat-icon">
                                                        <i className="fas fa-users"></i>
                                                    </div>
                                                    <div className="stat-content">
                                                        <h3>{companyData.talentsContacted || 0}</h3>
                                                        <p>Talents Contacted</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="stat-card">
                                                    <div className="stat-icon">
                                                        <i className="fas fa-handshake"></i>
                                                    </div>
                                                    <div className="stat-content">
                                                        <h3>{companyData.successfulHires || 0}</h3>
                                                        <p>Successful Hires</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="stat-card">
                                                    <div className="stat-icon">
                                                        <i className="fas fa-percentage"></i>
                                                    </div>
                                                    <div className="stat-content">
                                                        <h3>{companyData.talentsContacted > 0
                                                            ? Math.round((companyData.successfulHires / companyData.talentsContacted) * 100)
                                                            : 0}%
                                                        </h3>
                                                        <p>Success Rate</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Quick Actions */}
                                        <div className="quick-actions">
                                            <h3>Quick Actions</h3>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <Link to="/talent-marketplace" className="action-card">
                                                        <i className="fas fa-search"></i>
                                                        <div>
                                                            <h5>Browse Talents</h5>
                                                            <p>Find new talent for your team</p>
                                                        </div>
                                                    </Link>
                                                </div>
                                                <div className="col-md-6">
                                                    <Link to="/contact" className="action-card">
                                                        <i className="fas fa-headset"></i>
                                                        <div>
                                                            <h5>Get Support</h5>
                                                            <p>Contact our support team</p>
                                                        </div>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Recent Activity */}
                                        <div className="recent-activity">
                                            <h3>Recent Activity</h3>
                                            {contactsLoading ? (
                                                <p className="text-muted">Loading talent contacts...</p>
                                            ) : contactedTalents.length === 0 ? (
                                                <p className="text-muted">No talent contacts yet. Hire from a graduate profile to track interest here.</p>
                                            ) : (
                                            <div className="activity-list">
                                                {contactedTalents.slice(0, 3).map(talent => (
                                                    <div key={talent.id} className="activity-item">
                                                        <div className="activity-avatar">
                                                            <i className="fas fa-user"></i>
                                                        </div>
                                                        <div className="activity-content">
                                                            <h6>Contacted {talent.name}</h6>
                                                            <p>{talent.title} - {talent.status}</p>
                                                            <span className="activity-date">{talent.date}</span>
                                                        </div>
                                                        <div className="activity-rating">
                                                            {[...Array(5)].map((_, i) => (
                                                                <i 
                                                                    key={i} 
                                                                    className={`fas fa-star ${i < talent.rating ? 'active' : ''}`}
                                                                ></i>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'talents' && (
                                    <div className="tab-content">
                                        <div className="content-header">
                                            <h2>My Talents</h2>
                                            <p>Manage your talent connections and hiring pipeline</p>
                                        </div>

                                        <div className="talents-table">
                                            {contactsLoading ? (
                                                <p className="text-muted">Loading talent contacts...</p>
                                            ) : contactedTalents.length === 0 ? (
                                                <p className="text-muted">No talent contacts yet.</p>
                                            ) : (
                                            <div className="table-responsive">
                                                <table className="table">
                                                    <thead>
                                                        <tr>
                                                            <th>Name</th>
                                                            <th>Position</th>
                                                            <th>Status</th>
                                                            <th>Date Contacted</th>
                                                            <th>Rating</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {contactedTalents.map(talent => (
                                                            <tr key={talent.id}>
                                                                <td>
                                                                    <div className="talent-info">
                                                                        <div className="talent-avatar">
                                                                            <i className="fas fa-user"></i>
                                                                        </div>
                                                                        <span>{talent.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{talent.title}</td>
                                                                <td>
                                                                    <span className={`status-badge ${talent.status.toLowerCase()}`}>
                                                                        {talent.status}
                                                                    </span>
                                                                </td>
                                                                <td>{talent.date}</td>
                                                                <td>
                                                                    <div className="rating">
                                                                        {[...Array(5)].map((_, i) => (
                                                                            <i 
                                                                                key={i} 
                                                                                className={`fas fa-star ${i < talent.rating ? 'active' : ''}`}
                                                                            ></i>
                                                                        ))}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="action-buttons">
                                                                        {talent.slug && (
                                                                            <Link
                                                                                to={`/graduates/${talent.slug}`}
                                                                                className="btn btn-sm btn-outline-secondary"
                                                                                title="View profile"
                                                                            >
                                                                                <i className="fas fa-eye"></i>
                                                                            </Link>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'messages' && (
                                    <div className="tab-content">
                                        <div className="content-header">
                                            <h2>Messages</h2>
                                            <p>Communicate with talents and manage conversations</p>
                                        </div>
                                        <div className="empty-state">
                                            <i className="fas fa-envelope fa-3x"></i>
                                            <h4>No messages yet</h4>
                                            <p>Start conversations with talents to see messages here.</p>
                                            <Link to="/talent-marketplace" className="btn" style={{ backgroundColor: '#8A038C', borderColor: '#8A038C', color: 'white' }}>
                                                Browse Talents
                                            </Link>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'settings' && (
                                    <div className="tab-content">
                                        <div className="content-header">
                                            <h2>Account Settings</h2>
                                            <p>Manage your company profile and subscription</p>
                                        </div>
                                        <div className="settings-sections">
                                            <div className="setting-section">
                                                <h4>Subscription Details</h4>
                                                <div className="setting-item">
                                                    <label>Current Plan</label>
                                                    <span>{companyData.plan}</span>
                                                </div>
                                                <div className="setting-item">
                                                    <label>Next Billing Date</label>
                                                    <span>{companyData.nextBilling}</span>
                                                </div>
                                                <div className="setting-item">
                                                    <label>Status</label>
                                                    <span className="status-active">{companyData.status}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyDashboard;
